import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { clearDistributorReview } from "@/lib/distributorReview";
import { isPlatformDesignerUser } from "@/lib/usePlatformDesigner";

const STORAGE_KEY = "visor:workspace_mode";

/** Modo exclusivo Platform Designer — independiente de despacho/distribuidor/comprador. */
export const WORKSPACE_MODE_PLATFORM = "platform";

const WorkspaceModeCtx = createContext(null);

/**
 * Modo Despacho: operación diaria como contador (cartera, confronta, etc.).
 * Modo Distribuidor: emisión de licencias y tareas de proveedor del software.
 * Modo Plataforma: superusuario BNT (platform_designer) — sin cartera ni comprador.
 */
export function WorkspaceModeProvider({ user, children, onRefreshUser }) {
  const isPlatformDesigner = isPlatformDesignerUser(user);

  const canSwitch = useMemo(() => {
    if (isPlatformDesigner) return false;
    if (user?.is_designer_backdoor && user?.is_admin_owner) return true;
    const designerOwner = Boolean(user?.is_admin_owner && user?.can_generate_licenses);
    if (designerOwner) return true;
    if (typeof user?.can_switch_workspace === "boolean") return user.can_switch_workspace;
    return false;
  }, [
    isPlatformDesigner,
    user?.can_switch_workspace,
    user?.is_admin_owner,
    user?.can_generate_licenses,
    user?.is_designer_backdoor,
  ]);

  const [storedMode, setStoredModeState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "distribuidor" || v === "despacho") return v;
    } catch { /* ignore */ }
    return "despacho";
  });

  const mode = isPlatformDesigner ? WORKSPACE_MODE_PLATFORM : storedMode;

  useEffect(() => {
    if (!isPlatformDesigner) return;
    clearDistributorReview();
  }, [isPlatformDesigner, user?.id]);

  useEffect(() => {
    if (isPlatformDesigner) return;
    if (!canSwitch && storedMode === "distribuidor") {
      setStoredModeState("despacho");
      try {
        localStorage.setItem(STORAGE_KEY, "despacho");
      } catch { /* ignore */ }
    }
  }, [canSwitch, storedMode, isPlatformDesigner]);

  const setMode = useCallback(
    async (m) => {
      if (isPlatformDesigner) return;
      if (m !== "despacho" && m !== "distribuidor") return;
      if (!canSwitch && m === "distribuidor") return;
      setStoredModeState(m);
      try {
        localStorage.setItem(STORAGE_KEY, m);
      } catch { /* ignore */ }
      if (typeof onRefreshUser === "function") {
        try {
          await onRefreshUser();
        } catch { /* ignore */ }
      }
      try {
        window.dispatchEvent(new CustomEvent("visor:workspace-mode-changed", { detail: { mode: m } }));
      } catch { /* ignore */ }
    },
    [canSwitch, onRefreshUser, isPlatformDesigner],
  );

  const value = useMemo(
    () => ({
      mode,
      setMode,
      canSwitch,
      isPlatformDesigner,
      isPlatformWorkspace: isPlatformDesigner,
    }),
    [mode, setMode, canSwitch, isPlatformDesigner],
  );

  return (
    <WorkspaceModeCtx.Provider value={value}>{children}</WorkspaceModeCtx.Provider>
  );
}

export function useWorkspaceMode() {
  const v = useContext(WorkspaceModeCtx);
  if (!v) {
    throw new Error("useWorkspaceMode must be used within WorkspaceModeProvider");
  }
  return v;
}

export function getStoredWorkspaceMode() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "distribuidor" || v === "despacho") return v;
  } catch { /* ignore */ }
  return "despacho";
}
